import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DEFAULT_VAT_MODE, type VatMode } from '@crm/shared'
import type { Request, RequestItem } from '../../../shared/api/types'
import { getQuotations } from '../../../shared/api/quotations.api'
import type { SelectedPrice } from '../hooks/useKpMode'
import type { MarkupConstants as MarkupConstantsBase } from '../../../shared/api/exchangeRates.api'
import { useKpBestPrices } from '../hooks/useKpBestPrices'
import { useKpDownload } from '../hooks/useKpDownload'
import { KPPreviewModal } from './KPPreviewModal'
import { KpDownloadModal } from './KpDownloadModal'
import { KP_VAT_TOGGLE_ENABLED, KpVatModeToggle } from './KpVatModeToggle'

// Панель КП рендерится только после гейта готовности цен, поэтому курсы и
// коэффициенты здесь обязательны — фолбэков вида `?? 12.5` больше нет.
type ExchangeRates = { USD: number; EUR: number; CNY: number }
type MarkupConstants = MarkupConstantsBase

type Props = {
  request: Request
  visibleItems: RequestItem[]
  selectedPrices: Map<string, SelectedPrice>
  setSelectedPrices: React.Dispatch<React.SetStateAction<Map<string, SelectedPrice>>>
  kpMarkups: Record<string, number>
  setKpMarkups: React.Dispatch<React.SetStateAction<Record<string, number>>>
  positionOverrides: Record<string, number>
  setPositionOverrides: React.Dispatch<React.SetStateAction<Record<string, number>>>
  showOverrides: boolean
  setShowOverrides: React.Dispatch<React.SetStateAction<boolean>>
  onCancel: () => void
  exchangeRates: ExchangeRates
  markupConstants: MarkupConstants
}

const round2 = (n: number) => Math.round(n * 100) / 100
const round4 = (n: number) => Math.round(n * 10000) / 10000

/**
 * Одна строка блока «Индивидуальная наценка»: партномер + наценка + цена для заказчика.
 * Двусторонняя привязка: меняешь наценку → пересчитывается цена; меняешь цену → наценка
 * пересчитывается (markup = цена / себестоимость). Источник правды — positionOverrides
 * (коэффициент наценки), цена всегда производная и равна той, что уйдёт в КП.
 */
function OverrideRow({
  rowNumber,
  partNumber,
  cost,
  defaultMarkup,
  override,
  onSetMarkup,
  onClear,
}: {
  rowNumber: number | undefined
  partNumber: string
  cost: number
  defaultMarkup: number
  override: number | undefined
  onSetMarkup: (markup: number) => void
  onClear: () => void
}) {
  const hasOverride = override !== undefined
  const effectiveMarkup = override ?? defaultMarkup
  const hasCost = cost > 0
  const customerPrice = hasCost ? round2(cost * effectiveMarkup) : 0
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-6 text-right shrink-0">{rowNumber ?? '—'}</span>
      <span className="text-xs font-mono text-gray-700 w-40 truncate" title={partNumber}>
        {partNumber}
      </span>
      {/* Наценка (коэффициент) */}
      <input
        type="number"
        step="0.01"
        placeholder={round4(defaultMarkup).toString()}
        value={hasOverride ? round4(override) : ''}
        onChange={(e) => {
          const v = parseFloat(e.target.value)
          if (isNaN(v) || e.target.value === '') onClear()
          else onSetMarkup(v)
        }}
        title="Коэффициент наценки. Изменишь — пересчитается цена для заказчика."
        className={`w-20 px-2 py-0.5 border rounded text-xs text-center focus:outline-none focus:ring-1 ${
          hasOverride
            ? 'border-amber-400 bg-amber-50 focus:ring-amber-400'
            : 'border-gray-300 focus:ring-brand-500'
        }`}
      />
      {/* Цена для заказчика (₽, с наценкой) — ровно та, что попадёт в КП */}
      <input
        type="number"
        step="0.01"
        disabled={!hasCost}
        value={hasCost ? customerPrice : ''}
        placeholder={hasCost ? '' : '—'}
        onChange={(e) => {
          if (!hasCost) return
          const p = parseFloat(e.target.value)
          if (isNaN(p) || e.target.value === '') onClear()
          else onSetMarkup(p / cost)
        }}
        title={
          hasCost
            ? 'Цена для заказчика, ₽ (с наценкой). Введи целевую цену — наценка пересчитается.'
            : 'Цена не выбрана'
        }
        className="w-24 px-2 py-0.5 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-100 disabled:text-gray-400"
      />
      {hasOverride ? (
        <button onClick={onClear} className="text-xs text-gray-400 hover:text-red-500">
          ×
        </button>
      ) : (
        <span className="w-3" />
      )}
    </div>
  )
}

export function KpModePanel({
  request,
  visibleItems,
  selectedPrices,
  setSelectedPrices,
  kpMarkups,
  setKpMarkups,
  positionOverrides,
  setPositionOverrides,
  showOverrides,
  setShowOverrides,
  onCancel,
  exchangeRates,
  markupConstants,
}: Props) {
  const visibleItemsCount = visibleItems.length
  const [showPreview, setShowPreview] = useState(false)
  const [showDownload, setShowDownload] = useState(false)
  // Примечания к позициям КП (по itemId) — вводятся в предпросмотре, попадают в
  // Excel и PDF. Живут на время режима КП; в snapshot не пишутся (только в файл).
  const [notes, setNotes] = useState<Record<string, string>>({})

  // Режим НДС: пока менеджер не переключил — как в последнем КП этой заявки, у первого КП —
  // «С НДС» (решение 15.10.2026, docs/features/kp-vat-mode.md). Список КП — общий кэш страницы.
  const { data: quotations } = useQuery({
    queryKey: ['quotations', request.id],
    queryFn: () => getQuotations(request.id),
    enabled: KP_VAT_TOGGLE_ENABLED,
  })
  const [vatOverride, setVatOverride] = useState<VatMode | null>(null)
  // Переключатель скрыт (KP_VAT_TOGGLE_ENABLED) — КП всегда «С НДС».
  const vatMode: VatMode = KP_VAT_TOGGLE_ENABLED
    ? (vatOverride ?? quotations?.[0]?.vatMode ?? DEFAULT_VAT_MODE)
    : DEFAULT_VAT_MODE

  // Номер позиции = порядок в таблице позиций выше (visibleItems — тот же
  // массив, что кормит AG Grid; у менеджера сортировка выключена, поэтому
  // индекс + 1 совпадает с колонкой «№» таблицы).
  const rowNumberByItemId = useMemo(() => {
    const map = new Map<string, number>()
    visibleItems.forEach((item, idx) => map.set(item.id, idx + 1))
    return map
  }, [visibleItems])

  // Автоподбор лучших (минимальных в ₽) цен по всем видимым позициям и выбор
  // всех позиций. Цены квот для подписи строки КП поставщиком подгружаются из
  // сводки (№73) — см. useKpBestPrices.
  const {
    selectBest,
    selectAll,
    loading: selectingPrices,
  } = useKpBestPrices({
    requestId: request.id,
    visibleItems,
    exchangeRates,
    markupConstants,
    setSelectedPrices,
  })

  // Скачивание КП в выбранном формате (вызывается из модалки выбора формата):
  // снапшот на сервер + файл pdf/excel, после успеха — выход из режима КП.
  // Повтор после сбоя генерации файла второй снапшот не создаёт (useKpDownload).
  const { handleDownloadKp } = useKpDownload({
    request,
    selectedPrices,
    kpMarkups,
    positionOverrides,
    exchangeRates,
    markupConstants,
    notes,
    vatMode,
    onDone: onCancel,
  })

  return (
    <div className="flex-1 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-green-700">Режим КП</span>
        <span className="text-xs text-gray-500">Нажмите на цены в таблице для выбора</span>
        <button
          onClick={() => void selectBest()}
          disabled={visibleItemsCount === 0 || selectingPrices}
          className="px-2.5 py-1 text-xs font-medium rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 transition-colors"
          title="Выбрать минимальные цены в рублях по всем позициям. Потом можно переключить отдельные ячейки вручную."
        >
          По лучшим ценам
        </button>
        <button
          onClick={() => void selectAll()}
          disabled={visibleItemsCount === 0 || selectingPrices}
          className="px-2.5 py-1 text-xs font-medium rounded-lg border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition-colors"
          title="Выбрать ВСЕ позиции, включая те, по которым нет ни одной цены — они попадут в КП с ценой 0. Уже выбранные вручную — не перезаписываются."
        >
          Выбрать все позиции
        </button>
        <span className="text-xs font-medium text-green-600 ml-2">
          Выбрано: {selectedPrices.size} из {visibleItemsCount}
        </span>
        {KP_VAT_TOGGLE_ENABLED && (
          <div className="ml-auto">
            <KpVatModeToggle value={vatMode} onChange={setVatOverride} />
          </div>
        )}
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="text-xs font-medium text-gray-600 mb-2">Наценка</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {(
            [
              ['stock', 'Склад'],
              ['other', 'Стоки'],
              ['rf', 'РФ'],
              ['china', 'Китай'],
              ['manuf', 'Производители'],
            ] as const
          ).map(([key, label]) => (
            <div key={key}>
              <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
              <input
                type="number"
                step="0.01"
                value={kpMarkups[key]}
                onChange={(e) =>
                  setKpMarkups((prev) => ({
                    ...prev,
                    [key]: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          ))}
        </div>
      </div>
      {/* Индивидуальные наценки на позиции */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setShowOverrides((v) => !v)}
          className={`px-3 py-1 text-xs font-medium rounded-lg border transition-colors ${
            showOverrides
              ? 'bg-amber-50 border-amber-300 text-amber-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          Наценки на позиции
          {Object.keys(positionOverrides).length > 0
            ? ` (${Object.keys(positionOverrides).length})`
            : ''}
        </button>
        {Object.keys(positionOverrides).length > 0 && (
          <button
            onClick={() => setPositionOverrides({})}
            className="text-xs text-gray-400 hover:text-red-500"
          >
            Сбросить все
          </button>
        )}
      </div>
      {showOverrides && selectedPrices.size > 0 && (
        <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 space-y-2">
          <div className="text-xs font-medium text-amber-700 mb-1">Индивидуальная наценка</div>
          {/* Заголовки колонок: наценка ↔ цена для заказчика (двусторонний пересчёт) */}
          <div className="flex items-center gap-2 px-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700/70">
            <span className="w-6 text-right shrink-0">№</span>
            <span className="w-40">Артикул</span>
            <span className="w-20 text-center">Наценка</span>
            <span className="w-24 text-center">Цена, ₽</span>
            <span className="w-3" />
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {Array.from(selectedPrices.entries())
              .sort(
                ([a], [b]) =>
                  (rowNumberByItemId.get(a) ?? Infinity) - (rowNumberByItemId.get(b) ?? Infinity),
              )
              .map(([itemId, sel]) => {
                const item = request.items?.find((i) => i.id === itemId)
                if (!item) return null
                return (
                  <OverrideRow
                    key={itemId}
                    rowNumber={rowNumberByItemId.get(itemId)}
                    partNumber={item.partNumber}
                    cost={sel.cost}
                    defaultMarkup={kpMarkups[sel.matrix] ?? 1}
                    override={positionOverrides[itemId]}
                    onSetMarkup={(markup) =>
                      setPositionOverrides((prev) => ({ ...prev, [itemId]: markup }))
                    }
                    onClear={() =>
                      setPositionOverrides((prev) => {
                        const next = { ...prev }
                        delete next[itemId]
                        return next
                      })
                    }
                  />
                )
              })}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowDownload(true)}
          disabled={selectedPrices.size === 0}
          className="px-4 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          Скачать КП ({selectedPrices.size})
        </button>
        <button
          onClick={() => setShowPreview(true)}
          disabled={selectedPrices.size === 0}
          className="px-4 py-1.5 text-sm font-medium text-brand-700 border border-brand-300 bg-white rounded-lg hover:bg-brand-50 disabled:opacity-50 transition-colors"
          title="Открыть предпросмотр КП с финальными ценами для заказчика"
        >
          Предпросмотр КП
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Отменить
        </button>
      </div>
      {showPreview && (
        <KPPreviewModal
          request={request}
          visibleItems={visibleItems}
          selectedPrices={selectedPrices}
          kpMarkups={kpMarkups}
          setKpMarkups={setKpMarkups}
          positionOverrides={positionOverrides}
          notes={notes}
          setNotes={setNotes}
          vatMode={vatMode}
          setVatMode={setVatOverride}
          onClose={() => setShowPreview(false)}
          onDownload={async () => {
            // Из предпросмотра ведём в общий выбор формата/юрлица.
            setShowPreview(false)
            setShowDownload(true)
          }}
        />
      )}
      {showDownload && (
        <KpDownloadModal
          defaultEntityId={request.sellerEntityId}
          onClose={() => setShowDownload(false)}
          onPick={handleDownloadKp}
        />
      )}
    </div>
  )
}
